// WP-11 dashboard shared UI primitives (AgroStream). Keep these small and reused
// across Sites/Farms/Bands Views so styling and null-handling stay consistent.
import ShellModal from '../shell/drawer/ShellModal';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { formatMoney, formatDate } from '../utils/format';
import { focusStyle } from './FocusChips';
import api from '../services/api';
import HelpIcon from '../components/HelpIcon';
import { TASK_SEVERITIES, SEVERITY_CFG } from '../config/taskSeverity';
import TaskForm from '../components/Forms/TaskForm';
import { filterDormant, KIND } from '../utils/entityDormancy'; import { lineText } from '../shell/ActionIcons';
import { emoText } from '../shell/EmojiIcons';
  // WS2 slice 2: ONE dormancy predicate (doc 656/659)

// ── value formatting: null/undefined render as -- in grey, never 0 or blank ──
export const isNil = (v) => v === null || v === undefined || (typeof v === 'number' && Number.isNaN(v));
export const Dash = () => <span style={{ color: 'var(--muted2)' }}>--</span>;
export const money = (v) => isNil(v) ? <Dash /> : formatMoney(v);
export const intNum = (v) => isNil(v) ? <Dash /> : Math.round(v).toLocaleString('en-US');
export const pct = (v, d = 1) => isNil(v) ? <Dash /> : v.toFixed(d) + '%';

// ── phase/status badge ──
const PHASE_CLASS = {
  laying: 'badge-laying', growing: 'badge-growing',   // 603 B6 A4: dropped dead 'pre-lay'/'molting' keys (phase never emits them)
  retired: 'badge-retired', vacant: 'badge-vacant',
  inactive: 'badge-inactive', archived: 'badge-inactive', active: 'badge-laying',
  sanitation: 'badge-sanitation', reserved: 'badge-reserved', occupied: 'badge-laying',   // WP-13 S3a canonical seat-states (599 B3: 'occupied' seat token)
  decommissioned: 'badge-inactive',
  idle: 'badge-idle',   // owner v0.40: idle = a FREE seat carrying the idle directive (BlockGrid/SeatCard parity) — not a new seat_state
};
export function Badge({ value, tone, size = 'sm', dot = false, children }) {
  if (tone) {
    const safeTone = ['good', 'warn', 'crit', 'info', 'neutral'].includes(tone) ? tone : 'neutral';
    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, borderRadius: 20,
      padding: '2px 8px', fontSize: size === 'md' ? 12 : 11, fontWeight: 600, lineHeight: 1.4,
      background: `var(--status-${safeTone}-bg)`, color: `var(--status-${safeTone})`, whiteSpace: 'nowrap' }}>
      {dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: `var(--status-${safeTone}-dot)` }} />}
      {children ?? value}
    </span>;
  }
  const key = String(value || 'vacant').toLowerCase();
  const cls = PHASE_CLASS[key] || 'badge-vacant';
  const LABELS = { growing: 'Growing', sanitation: 'Sanitation-tail', reserved: 'Reserved', occupied: 'Occupied', vacant: 'Free', inactive: 'Inactive', idle: 'Idle' };   // 583 SLICE 2: canonical 'Growing'; 599 B3: seat tokens (Occupied/Free/Sanitation-tail/Inactive) read cleanly; owner v0.40: 'Idle'
  const label = LABELS[key] || (value || 'Free');
  return <span className={cls}>{emoText(label)}</span>;
}

// ── card container ──
export const Card = React.forwardRef(function Card({ children, style }, ref) {
  return (
    <div ref={ref} style={{ background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 9, padding: 14, ...style }}>
      {children}
    </div>
  );
});

// ── KPI tile ──
export function Kpi({ label, value, sub, chip, tone = 'neutral', spark, href, onClick, variant = 'default', icon }) {
  const clickable = Boolean(href || onClick);
  // v0.43: legacy callers (label/value/sub only) render EXACTLY as before — new look only when new props are used.
  const enhanced = Boolean(chip || spark || href || onClick || icon || variant !== 'default');
  if (!enhanced) {
    return (
      <div style={{ background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 9, padding: '10px 14px', minWidth: 0 }}>
        <div style={{ fontSize: 9.5, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 4 }}>{emoText(label)}</div>
        <div className="tabular-nums" style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>{value}</div>
        {sub != null && <div style={{ fontSize: 10.5, color: 'var(--muted2)', marginTop: 2 }}>{sub}</div>}
      </div>
    );
  }
  return (
    <div onClick={onClick} role={clickable ? 'button' : undefined} tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick?.(e); } : undefined}
      style={{ background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 10, padding: variant === 'compact' ? '10px 12px' : '14px 15px', minWidth: 0,
        cursor: clickable ? 'pointer' : 'default', transition: 'transform .15s, box-shadow .15s', position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--label)', marginBottom: 7 }}>
        {icon && <Icon name={icon} size={16} />}<span>{emoText(label)}</span>{clickable && <span style={{ marginLeft: 'auto', color: 'var(--green)', fontSize: 16 }}>→</span>}
      </div>
      <div className="tabular-nums" style={{ fontSize: variant === 'compact' ? 20 : 24, fontWeight: 700, color: 'var(--ink)' }}>{value}</div>
      {(chip || sub != null || spark) && <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 7 }}>
        {chip && <Badge tone={tone} value={chip} dot={false} />}
        {sub != null && <div style={{ fontSize: 10.5, color: 'var(--muted2)' }}>{sub}</div>}
        {spark && variant !== 'compact' && <div style={{ marginLeft: 'auto', color: 'var(--green)' }}>{spark}</div>}
      </div>}
    </div>
  );
}
export function KpiStrip({ children, cols }) {
  return <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols || 'auto-fit'}, minmax(120px, 1fr))`, gap: 10 }}>{children}</div>;
}

// ── breadcrumb ──
export function Breadcrumb({ items }) {
  return (
    <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>
      {items.map((it, i) => (
        <span key={i}>
          {i > 0 && <span style={{ color: 'var(--muted2)' }}> › </span>}
          {it.onClick
            ? <span onClick={it.onClick} style={{ cursor: 'pointer', color: 'var(--green)', fontWeight: 600 }}>{lineText(it.label)}</span>
            : <span style={{ color: 'var(--ink)', fontWeight: 600 }}>{emoText(it.label)}</span>}
        </span>
      ))}
    </div>
  );
}

// ── sub-header tabs ──
// P1-6: sub-header tab strip. Each tab is { id, label, badge? } -- `badge` renders a
// low-key child-level hint chip next to the label (e.g. Overview -> "Farms").
export function Tabs({ tabs, active, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--line)', marginBottom: 16, flexWrap: 'nowrap', overflowX: 'auto' }}>
      {tabs.map(t => {
        const on = t.id === active;
        return (
          <button data-borderless="tab" key={t.id} onClick={() => onChange(t.id)} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '10px 14px', fontSize: 12,
            whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0,
            fontWeight: on ? 700 : 500, color: on ? '#1b4332' : '#7a8a7f',
            borderBottom: on ? '2px solid #2d6a4f' : '2px solid transparent',
          }}>
            {lineText(t.label)}
            {t.badge && (
              <span style={{
                marginLeft: 1, fontSize: 9, fontWeight: 700, padding: '1px 6px',
                borderRadius: 8, background: '#e8440a', color: '#fff', verticalAlign: 'middle',
              }}>{t.badge}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// P1-6: placeholder for tabs whose content lands in a later alignment phase.
export function StubTab({ name }) {
  return <Card><div style={{ color: 'var(--muted)' }}>{name} tab — coming in Phase 3 of the alignment pass.</div></Card>;
}

// ── page header ──
export function PageHeader({ title, subtitle, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 6 }}>
          {emoText(title)}
          {/* build 60: lens-title help keys (content deferred) */}
          {({ Sites: 'concept.dashboard_sites', Farms: 'concept.dashboard_farms', Bands: 'concept.dashboard_bands' })[title] && (
            <HelpIcon helpKey={({ Sites: 'concept.dashboard_sites', Farms: 'concept.dashboard_farms', Bands: 'concept.dashboard_bands' })[title]} />
          )}
        </h1>
        {subtitle && <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 2 }}>{subtitle}</div>}
      </div>
      {right}
    </div>
  );
}

// ── work queue (right column) ── P2-7: single card, 3 colored blocks (ALERTS /
// UPCOMING / TASKS) matching the .dc work-queue. Each block is focus-dimmable
// (tagged 'alerts' -> emphasized under Problems/To-do, dimmed under Ops/Health/
// Finance, normal under Monitor). Alerts content stays real (expandable AlertRow).
const UPCOMING_MAX = 20;
export function WorkQueue({ alerts, onOpenBand, scope, focus = 'monitor', taskScope, domain }) {
  const fired = (alerts || []).filter(a =>
    a.alert_bird_stability || a.alert_production_drop || a.alert_mortality_spike ||
    a.alert_feed_spike || a.alert_feed_stock_low || a.alert_cost_per_egg);
  // Each block collapses/expands; a count badge sits next to the title; the content
  // area scrolls so a long list never blows out the card height.
  const [open, setOpen] = useState({ alerts: true, upcoming: false, tasks: false });
  const tasksOpenRef = useRef(false); tasksOpenRef.current = open.tasks;
  const toggle = (k) => setOpen(o => ({ ...o, [k]: !o[k] }));

  // WP-12 Step I.1: personal user tasks, EXACT-scope (no roll-up). A specific
  // Band/Farm/Site detail shows only that node's tasks. An entity LIST view passes
  // its kind with no id -> all tasks of that kind (any node). Global (unscoped)
  // tasks never appear in a Work Queue panel — they live only in the To-do tab.
  const ts = taskScope || { scope: 'org' };
  const [tasks, setTasks] = useState([]);
  const [doneTasks, setDoneTasks] = useState([]);
  const [showDone, setShowDone] = useState(false);
  const [formTask, setFormTask] = useState(undefined); // undefined=closed, null=add, task=edit
  const [taskRef, setTaskRef] = useState({ bands: [], farms: [], sites: [] });
  const isKind = ['band', 'farm', 'site'].includes(ts.scope);
  const hasNode = isKind && ts.scopeId;
  const domainQs = domain ? `&domain=${domain}` : '';
  const taskQs = hasNode ? `?scope=${ts.scope}&scope_id=${ts.scopeId}` : '?scope=org';
  const [upcoming, setUpcoming] = useState(null);   // null = the live list is not loaded (the block is closed): the badge shows the cached count
  const [upcomingCount, setUpcomingCount] = useState(0);
  const wqScope = hasNode ? `scope=${ts.scope}&scope_id=${ts.scopeId}` : 'scope=org';
  // Round 2a (M3): page open reads only the COUNT (GET /work-queue/count, cached 45 s per user + scope, cleared by any write); the live
  // org-wide compute (GET /work-queue) runs only when UPCOMING is opened. Display only.
  const loadUpcoming = useCallback(() => {
    if (!domain) return;
    api.request(`/work-queue?${wqScope}&domain=${domain}`)
      .then(d => setUpcoming((d.upcoming || []).slice(0, UPCOMING_MAX))).catch(() => setUpcoming([]));
  }, [wqScope, domain]);
  const taskFilt = useCallback((arr) => {
      arr = arr || [];
      if (hasNode) return arr;                                    // specific node (server exact-filtered)
      if (isKind) return arr.filter(t => t.scope_type === ts.scope); // entity list -> all of that kind
      return arr.filter(t => !t.scope_type);                      // org fallback -> Global only
  }, [hasNode, isKind, ts.scope]);
  const loadDone = useCallback(() => { api.request(`/tasks${taskQs}${domainQs}&done=1`).then(d => setDoneTasks(taskFilt(d))).catch(() => setDoneTasks([])); }, [taskQs, domainQs, taskFilt]);
  const loadTasks = useCallback(() => {
    api.request(`/tasks${taskQs}${domainQs}`).then(d => setTasks(taskFilt(d))).catch(() => setTasks([]));
    if (tasksOpenRef.current) loadDone();   // Round 4c: completed tasks only show inside the TASKS block, which starts closed: read them when it opens
    if (domain) {
      setUpcoming(null);   // an opened UPCOMING block re-reads the live list (tasks saved / scope changed), a closed one keeps only the count
      api.request(`/work-queue/count?${wqScope}&domain=${domain}`)
        .then(d => setUpcomingCount(d && d.upcoming != null ? Math.min(d.upcoming, UPCOMING_MAX) : 0)).catch(() => setUpcomingCount(0));   // the list shows at most UPCOMING_MAX
    }
  }, [taskQs, domainQs, domain, wqScope, loadDone, taskFilt]);
  useEffect(() => { loadTasks(); }, [loadTasks]);
  useEffect(() => { setDoneTasks([]); }, [taskQs, domainQs]);   // another scope never shows the last scope's completed tasks
  useEffect(() => { if (open.tasks) loadDone(); }, [open.tasks, loadDone]);
  useEffect(() => { if (open.upcoming && upcoming === null) loadUpcoming(); }, [open.upcoming, upcoming, loadUpcoming]);
  // Ref lists for the popover's Scope/Target selectors (loaded once, lazily).
  useEffect(() => {
    if (formTask === undefined || taskRef.bands.length || taskRef.farms.length || taskRef.sites.length) return;
    Promise.all([api.request('/bands').catch(() => []), api.getFarmsLite().catch(() => []), api.request('/sites').catch(() => [])])
      .then(([b, f, s]) => setTaskRef({
        // Delivery: /api/bands returns ALL bands -> client exclusion required here.
        // 686: the explicit `band_status !== 'cancelled'` term is GONE — cancelled joined the BAND
        // dormancy set, so filterDormant covers it. One definition, no extra term.
        bands: filterDormant(b, KIND.BAND, false),
        // farms/sites come from plain /farms and /sites, which hide dormant rows SERVER-side — a client
        // filter here would be inert. Left deliberately (doc 660 pass 1).
        farms: f || [], sites: s || [],
      }));
  }, [formTask]); // eslint-disable-line react-hooks/exhaustive-deps
  // Seed scope for a new task: specific node -> that node; entity list -> that kind
  // (pick a target); org -> Global.
  const taskFormScope = hasNode ? { mode: ts.scope, id: ts.scopeId }
    : isKind ? { mode: ts.scope, id: '' } : { mode: 'global', id: '' };
  const block = (key, color, label, count, children) => {
    const isOpen = open[key];
    return (
      <div style={{ ...wqBlk, ...focusStyle(focus, key, 'block') }}>
        <div onClick={() => toggle(key)} style={{ ...wqBlkHead, color, cursor: 'pointer', justifyContent: 'space-between', marginBottom: isOpen ? 5 : 0 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>● {emoText(label)}</span>
            <span style={wqBadge}>{count}</span>
          </span>
          <span style={{ color: '#b6c0b8', fontSize: 9, fontWeight: 700 }}>{isOpen ? '▾' : '▸'}</span>
        </div>
        {isOpen && <div style={wqBlkBody}>{children}</div>}
      </div>
    );
  };
  return (
    <div style={wqCard}>
      <div style={wqHeader}>Work Queue {scope && <span style={{ fontWeight: 500, color: '#a3b0a7', fontSize: 11 }}>· {scope}</span>}</div>
      <div style={wqBody}>
        {block('alerts', '#b14a30', <>ALERTS <span onClick={e => e.stopPropagation()}><HelpIcon helpKey="concept.workqueue_alerts" /></span></>, fired.length, fired.length === 0
          ? <div style={wqStub}>No active alerts.</div>
          : fired.map(a => <AlertRow key={a.band_id} a={a} onOpenBand={onOpenBand} />))}
        {block('upcoming', '#33598c', <>UPCOMING <span onClick={e => e.stopPropagation()}><HelpIcon helpKey="concept.workqueue_upcoming" /></span></>, domain ? (upcoming ? upcoming.length : upcomingCount) : 0,
          domain
            ? (upcoming === null ? <div style={wqStub}>Loading…</div> : upcoming.length === 0 ? <div style={wqStub}>Nothing upcoming.</div>
              : upcoming.map((u, i) => (
                <div key={i} style={{ fontSize: 12, padding: '4px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#374151' }}>{emoText(u.message || u.label || u.title || '—')}</span>
                  <span style={{ fontSize: 11, color: '#6b7280' }}>{u.due_date || u.date || u.scheduled_date || (u.days_until != null ? `in ${u.days_until}d` : '')}</span>
                </div>)))
            : <div style={wqStub}>Nothing upcoming.</div>)}
        {block('tasks', '#2d6a4f',
          <>TASKS <span onClick={e => e.stopPropagation()}><HelpIcon helpKey="concept.user_tasks" /></span></>,
          tasks.length,
          <TasksBody tasks={tasks} doneTasks={doneTasks} showDone={showDone}
            onToggleShowDone={() => setShowDone(s => !s)} onChanged={loadTasks}
            onAdd={() => setFormTask(null)} onEdit={(t) => setFormTask(t)} />)}
      </div>
      {formTask !== undefined && (
        <TaskPopover title={formTask ? 'Edit task' : 'New task'} onClose={() => setFormTask(undefined)}>
          <TaskForm refData={taskRef} scope={taskFormScope} initial={formTask || undefined}
            defaultDomain={domain}
            onSaved={() => { setFormTask(undefined); loadTasks(); }} onCancel={() => setFormTask(undefined)} />
        </TaskPopover>
      )}
    </div>
  );
}

// Centered modal for the Work Queue "+ Add task" full editor (chosen over an inline
// panel so the narrow Work Queue card isn't crowded).
function TaskPopover({ children, onClose, title = 'New task' }) {
  return <ShellModal title={emoText(title)} size="sm" onClose={onClose} footer={false}>{children}</ShellModal>;
}
const wqCard = { flex: 1, height: '100%', background: '#fff', border: '1px solid #e4e8e3', borderRadius: 10, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' };
const wqHeader = { padding: '11px 14px 8px', fontSize: 12, fontWeight: 700, color: '#1b4332', borderBottom: '1px solid #eef1ee', display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 };
const wqBody = { flex: 1, overflow: 'auto', padding: '9px 12px 12px', display: 'flex', flexDirection: 'column', gap: 11 };
const wqBlk = { transition: 'opacity .15s, filter .15s' };
// No per-block scroll: the single wqBody scrolls only when all blocks combined
// overflow the card height (so a short list never scrolls while space remains).
const wqBlkBody = {};
const wqBadge = { fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8, background: '#eef1ee', color: '#5a635c', lineHeight: 1.4 };
const wqBlkHead = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 10.5, fontWeight: 700, letterSpacing: '.05em', marginBottom: 5 };
const wqStub = { fontSize: 11.5, color: '#a3b0a7' };

// Sticky top chrome for View B pages (identity bar + KPI strip + tabs). Full-bleed into
// <main>'s padding and pinned to the top; the fixed region ends at the tab strip's bottom
// border (the tabs' own marginBottom collapses out as a scroll-collapsing gap).
export const stickyChrome = {
  position: 'sticky', top: -24, zIndex: 20,
  margin: '-24px -24px 0', padding: '24px 24px 0', background: 'var(--bg)',
};

// SF-82: expandable alert row. Band name navigates (onOpenBand); the rest of the row
// toggles the drill-down. Magnitude data (from mv_alerts) is shown only for fired flags.
function AlertRow({ a, onOpenBand }) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const pct = (v) => v == null ? '--' : `${v > 0 ? '+' : ''}${(+v).toFixed(1)}%`;
  const nFmt = (v) => v == null ? '--' : (+v).toLocaleString('en-US', { maximumFractionDigits: 1 });
  const rows = [];
  if (a.alert_mortality_spike) { rows.push(['Mortality rate', pct(a.mortality_rate_pct)], ['Recent deaths/day', nFmt(a.recent_mortality_per_day)]); }
  if (a.alert_production_drop)  { rows.push(['Production change', pct(a.production_change_pct)], ['Recent eggs/day', nFmt(a.recent_eggs_per_day)]); }
  if (a.alert_feed_stock_low)  { rows.push(['Feed days remaining', a.feed_days_remaining == null ? '--' : nFmt(a.feed_days_remaining)]); }
  if (a.alert_feed_spike)      { rows.push(['Feed cost change', pct(a.feed_cost_change_pct)], ['Recent feed cost/day', nFmt(a.recent_feed_cost_per_day)]); }
  if (a.alert_bird_stability)  { rows.push(['Bird-count change', pct(a.bird_change_pct)], ['Current birds', nFmt(a.current_birds)]); }
  if (a.alert_cost_per_egg)    { rows.push(['Cost/egg change', pct(a.cost_per_egg_change_pct)]); }
  return (
    <div style={{ padding: '7px 0', borderTop: '1px solid var(--line2)', fontSize: 12 }}>
      <div onClick={() => setOpen(o => !o)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
        <span onClick={(e) => { e.stopPropagation(); onOpenBand && onOpenBand(a.band_id); }}
          onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
          style={{ fontWeight: 600, color: 'var(--green)', cursor: 'pointer', textDecoration: hover ? 'underline' : 'none' }}>
          {a.band_name || a.band_id}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {a.alert_health_combined && <span title="Composite health alert" style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--red)', display: 'inline-block' }} />}
          <span style={{ color: 'var(--muted2)', fontSize: 11 }}>{open ? '▾' : '▸'}</span>
        </span>
      </div>
      <div onClick={() => setOpen(o => !o)} style={{ color: 'var(--amber)', fontSize: 11, cursor: 'pointer' }}>{alertSummary(a)}</div>
      {open && (
        <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px dashed var(--line2)' }}>
          {rows.map(([label, val], i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '2px 0' }}>
              <span style={{ color: 'var(--muted)' }}>{emoText(label)}</span>
              <span className="tabular-nums" style={{ color: 'var(--ink)', fontWeight: 600 }}>{val}</span>
            </div>
          ))}
          {onOpenBand && <div onClick={(e) => { e.stopPropagation(); onOpenBand(a.band_id); }} style={{ marginTop: 6, fontSize: 11, fontWeight: 600, color: 'var(--green)', cursor: 'pointer' }}>→ Open band</div>}
        </div>
      )}
    </div>
  );
}
// WP-12 Step I/I.1: TASKS block body — open-task list (severity/overdue badges,
// urgent-first), done checkbox, per-row Edit (opens the popover prefilled) + Details
// (expands notes) + Delete, "+ Add task", and a Show-completed toggle.
function TasksBody({ tasks, doneTasks, showDone, onToggleShowDone, onChanged, onAdd, onEdit }) {
  const [err, setErr] = useState('');
  const [detailsId, setDetailsId] = useState(null);
  const sevRank = (t) => TASK_SEV_RANK[t.severity] ?? 1;
  const ordered = [...tasks].sort((a, b) => (sevRank(a) - sevRank(b))
    || ((a.due_date || '9999') < (b.due_date || '9999') ? -1 : (a.due_date || '9999') > (b.due_date || '9999') ? 1 : 0));

  async function toggleDone(t, val) {
    try { await api.setTaskDone(t.id, val); onChanged(); } catch (e) { setErr(e.message); }
  }
  async function del(t) {
    if (!window.confirm(`Delete task "${t.title}"?`)) return;
    try { await api.deleteTask(t.id); onChanged(); } catch (e) { setErr(e.message); }
  }

  const row = (t) => {
    const cfg = SEVERITY_CFG[t.severity] || SEVERITY_CFG.normal;
    const showDet = detailsId === t.id;
    return (
      <div key={t.id} style={{ padding: '6px 0', borderTop: '1px solid var(--line2)', fontSize: 11.5 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
          <input type="checkbox" checked={t.is_done} onChange={e => toggleDone(t, e.target.checked)} style={{ marginTop: 2, cursor: 'pointer' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: 'var(--ink2)', fontWeight: 400, textDecoration: t.is_done ? 'line-through' : 'none', overflow: 'hidden', textOverflow: 'ellipsis' }}>{emoText(t.title)}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', marginTop: 3 }}>
              <span style={{ ...taskBadge, background: cfg.bg, color: cfg.color }}>{emoText(cfg.label)}</span>
              {t.is_overdue && <span style={{ ...taskBadge, background: 'var(--red-bg)', color: 'var(--red)' }}>Overdue</span>}
              {t.due_date && <span style={{ fontSize: 10, color: 'var(--muted2)' }}>due {formatDate(t.due_date)}</span>}
              {t.scope_name && <span style={{ ...taskBadge, background: '#eef1ee', color: '#5a635c' }}>{t.scope_name}</span>}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              {t.notes && <span onClick={() => setDetailsId(showDet ? null : t.id)} style={taskLink}>{showDet ? 'Hide' : 'Details'}</span>}
              <span onClick={() => onEdit(t)} style={taskLink}>Edit</span>
              <span onClick={() => del(t)} style={{ ...taskLink, color: 'var(--red)' }}>Delete</span>
            </div>
            {showDet && t.notes && (
              <div style={{ marginTop: 4, fontSize: 10.5, color: 'var(--muted)', background: '#fafbfa', borderRadius: 6, padding: '5px 8px' }}>{t.notes}</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {ordered.length === 0 && <div style={wqStub}>No open tasks.</div>}
      {ordered.map(row)}
      {showDone && doneTasks.map(row)}
      {err && <div style={{ color: 'var(--red)', fontSize: 10.5, marginTop: 4 }}>{err}</div>}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
        <button className="btn-ghost" onClick={onAdd} style={{ padding: '3px 10px', fontSize: 11 }}>+ Add task</button>
        <button type="button" onClick={onToggleShowDone} className="btn sm">
          {showDone ? 'Hide completed' : `Show completed (${doneTasks.length})`}
        </button>
      </div>
    </div>
  );
}
const TASK_SEV_RANK = Object.fromEntries(TASK_SEVERITIES.map(s => [s, SEVERITY_CFG[s].rank]));
const taskBadge = { fontSize: 9.5, fontWeight: 700, padding: '1px 6px', borderRadius: 5, lineHeight: 1.5, whiteSpace: 'nowrap' };
const taskLink = { fontSize: 10.5, fontWeight: 600, color: 'var(--green)', cursor: 'pointer' };

function alertSummary(a) {
  const t = [];
  if (a.alert_mortality_spike) t.push('Mortality spike');
  if (a.alert_production_drop) t.push('Production drop');
  if (a.alert_feed_stock_low)  t.push('Feed stock low');
  if (a.alert_feed_spike)      t.push('Feed cost spike');
  if (a.alert_bird_stability)  t.push('Bird-count change');
  if (a.alert_cost_per_egg)    t.push('Cost/egg spike');
  return t.join(' · ') || 'Alert';
}

export const S = {
  // Table styles aligned to Gestion Avicole.dc.html REGION 3 (l.573-589):
  // #fafbfa header row, th 8px 14px / 10.5px / 600 / .04em / #94a399 (no uppercase),
  // td 9px 14px / border #f1f4f1 / 12px / #44524a. Dashboard-layer only.
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: { textAlign: 'left', padding: '8px 14px', fontSize: 10.5, fontWeight: 600, letterSpacing: '.04em', color: '#94a399', borderBottom: '1px solid #e4e8e3', whiteSpace: 'nowrap', background: '#fafbfa' },
  thr: { textAlign: 'right', padding: '8px 14px', fontSize: 10.5, fontWeight: 600, letterSpacing: '.04em', color: '#94a399', borderBottom: '1px solid #e4e8e3', whiteSpace: 'nowrap', background: '#fafbfa' },
  td: { padding: '9px 14px', borderBottom: '1px solid #f1f4f1', color: '#44524a', fontSize: 12 },
  tdr: { padding: '9px 14px', borderBottom: '1px solid #f1f4f1', color: '#44524a', fontSize: 12, textAlign: 'right' },
  rowClick: { cursor: 'pointer' },
  wqHead: { fontSize: 10, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--muted)', fontWeight: 700, marginBottom: 6 },
  stub: { fontSize: 12, color: 'var(--muted2)' },
  two: { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, alignItems: 'start' },
  filterBar: { display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' },
  select: { padding: '6px 9px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 12, background: '#fff', color: 'var(--ink)' },
  note: { fontSize: 11, color: 'var(--muted2)', marginTop: 10, lineHeight: 1.5 },
};

// ── v0.43 shell primitives. Legacy consumers above keep their APIs; these additions
// are intentionally self-contained until page reparenting begins. ──────────────────
export const CHART_SERIES = ['#2d6a4f', '#40916c', '#52b788', '#33598c', '#c47d10', '#94a399'];
export const CHART_THEME = {
  axis: { fill: '#a3b0a7', fontSize: 10.5, fontFamily: 'Inter' },
  grid: { stroke: '#eef1ed', vertical: false },
  tooltip: { background: '#fff', border: '1px solid var(--line)', borderRadius: 9, fontSize: 12 },
};

export function View({ tier = 'read', children, style }) {
  const widths = { focus: 'var(--w-focus)', read: 'var(--w-read)', wide: 'var(--w-wide)', full: 'var(--w-max)' };
  return <div className={`view view--${tier}`} style={{ width: '100%', maxWidth: widths[tier] || widths.read, marginInline: 'auto', paddingInline: 'var(--gutter)', ...style }}>{children}</div>;
}

const ICONS = { customer: ":user:", supplier: ":dispatch:", feed: ":feed:", band: ":flock:", flock: ":flock:", site: ":site:", farm: ":farm:", invoice: ":invoice:", bill: ":invoice:", payment: ":money:", stock: ":delivery:", item: ":delivery:", transfer: ":refresh:", expense: ":calc:", report: ":chart:", alert: ":warn:", settings: ":gear:", home: ":home:" };
export function Icon({ name, size = 18, label }) {
  // TODO(v0.43): swap this stable concept registry to the bundled OpenMoji SVG subset.
  return <span role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true} style={{ display: 'inline-flex', width: size, height: size, alignItems: 'center', justifyContent: 'center', fontSize: size, lineHeight: 1, flex: '0 0 auto' }}>{emoText(ICONS[name] || name || '•')}</span>;
}

export function ChartFrame({ title, sub, legend, children, style }) {
  return <section style={{ background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 10, padding: 15, ...style }}>
    {(title || sub || legend) && <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
      {title && <strong style={{ color: 'var(--ink)', fontSize: 13 }}>{emoText(title)}</strong>}
      {sub && <span style={{ color: 'var(--muted2)', fontSize: 11 }}>{sub}</span>}
      {legend && <span style={{ display: 'flex', gap: 9, marginLeft: 'auto' }}>{legend.map(([legendLabel, color]) => <span key={legendLabel} style={{ color: 'var(--muted)', fontSize: 11 }}><i style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: color, marginRight: 4 }} />{legendLabel}</span>)}</span>}
    </div>}
    <div data-chart-theme="agrostream" style={{ minHeight: 180 }}>{children}</div>
  </section>;
}

export function Tooltip({ text, children, label = 'More information' }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false); const esc = (event) => { if (event.key === 'Escape') close(); };
    window.addEventListener('click', close); window.addEventListener('scroll', close, true); window.addEventListener('keydown', esc);
    return () => { window.removeEventListener('click', close); window.removeEventListener('scroll', close, true); window.removeEventListener('keydown', esc); };
  }, [open]);
  return <span style={{ display: 'inline-flex', position: 'relative' }} onClick={(event) => event.stopPropagation()}>
    {children || <button type="button" aria-label={label} aria-expanded={open} onClick={() => setOpen(v => !v)} style={{ width: 17, height: 17, padding: 0, borderRadius: '50%', border: '1px solid var(--muted2)', background: open ? 'var(--blue-bg)' : '#fff', color: 'var(--blue)', cursor: 'pointer', fontSize: 12, fontStyle: 'italic', fontWeight: 700 }}>i</button>}
    {open && <span role="tooltip" style={{ position: 'absolute', top: 23, left: 0, zIndex: 90, width: 'max-content', maxWidth: 260, padding: '9px 11px', borderRadius: 9, background: 'var(--ink)', color: '#fff', fontSize: 12, lineHeight: 1.5, boxShadow: '0 10px 24px rgba(0,0,0,.2)' }}>{lineText(text)}</span>}
  </span>;
}

export function Guide({ route, helpContent = [], onOpen }) {
  const rows = (helpContent || []).filter(row => row.route === route || row.route === `${route}/` || row.key?.startsWith(`${route}.`));
  const contents = rows.length ? rows : [{ title: 'Guide coming soon', body: 'Contextual help_content will be assembled here as each surface is migrated.' }];
  return <button type="button" onClick={() => onOpen?.(contents)} style={{ border: '1px solid var(--line)', background: '#fff', color: 'var(--green)', borderRadius: 7, padding: '6px 9px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>ⓘ Guide</button>;
}

